/**
 * JWT Token Interface - defines the structure of B2C ID token claims
 */
export interface JwtToken {
  // Standard JWT claims
  iss?: string; // Issuer
  sub?: string; // Subject
  aud?: string; // Audience
  exp?: number; // Expiration time
  iat?: number; // Issued at
  nbf?: number; // Not before
  
  // B2C User claims
  oid?: string; // Object ID (user identifier)
  mid?: string; // Manager ID (custom claim)
  given_name?: string; // Given name
  family_name?: string; // Family name
  name?: string; // Full name
  
  /**
   * EMAIL CLAIM OPTIONS - B2C CAN RETURN EMAIL IN DIFFERENT FORMATS
   */
  
  /**
   * Primary email claim (B2C standard)
   */
  emailAddress?: string;
  
  /**
   * Alternative email claim name
   */
  email?: string;
  
  /**
   * Array of email addresses (used by some B2C configurations)
   */
  emails?: string[];
  
  // Other possible claims
  [key: string]: any;
}

/**
 * EXACT JWT PARSING METHOD - DECODES THE B2C ID TOKEN
 * This matches the parseJwt implementation from the APIM project
 */
export function parseJwt(jwtToken: string): JwtToken {
  try {
    // Split JWT into parts (header.payload.signature)
    const base64Url = jwtToken.split(".")[1];
    
    // Decode base64url to base64
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    
    // Parse the JSON payload (using atob for browser environment)
    const decodedToken = JSON.parse(atob(base64));

    // Convert timestamp fields to proper format if needed
    if (decodedToken.exp) {
      decodedToken.exp = new Date(parseInt(decodedToken.exp) * 1000);
    }

    if (decodedToken.nbf) {
      decodedToken.nbf = new Date(parseInt(decodedToken.nbf) * 1000);
    }

    if (decodedToken.iat) {
      decodedToken.iat = new Date(parseInt(decodedToken.iat) * 1000);
    }

    return decodedToken;
  } catch (error) {
    console.error("Failed to parse JWT token:", error);
    return {};
  }
}

/**
 * EXACT EMAIL EXTRACTION LOGIC - THIS IS HOW EMAIL IS PARSED FROM B2C TOKEN
 * Matches the logic from aadServiceV2.ts
 */
export function extractEmailFromJwt(jwtToken: JwtToken): string | null {
  // 🔑 EMAIL EXTRACTION WITH FALLBACK LOGIC (same order as APIM project)
  const email = jwtToken.emailAddress || jwtToken.email || jwtToken.emails?.[0];
  return email || null;
}

/**
 * COMPLETE EMAIL FLOW FOR B2C LOGIN
 * Process MSAL response and extract email from ID token
 */
export function processB2CLogin(idToken: string): string | null {
  try {
    // Parse the JWT token
    const jwtPayload = parseJwt(idToken);
    
    // Extract email with fallback logic (same as APIM project)
    const email = extractEmailFromJwt(jwtPayload);
    
    if (email) {
      console.log("User email extracted:", email);
      return email;
    } else {
      console.warn("No email found in B2C token claims");
      return null;
    }
  } catch (error) {
    console.error("Failed to process B2C login:", error);
    return null;
  }
}